import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Monitor, FileEdit, Wrench, FileText, ArrowRight, ArrowLeft, Send,
  Plus, Trash2, Upload, X, AlertCircle,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const TYPE_OPTIONS = [
  { value: "it_support", label: "IT Support", desc: "Computer, software, hardware issues", icon: Monitor, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { value: "stationary", label: "Stationary", desc: "Office supplies, materials", icon: FileEdit, color: "text-amber-700 bg-amber-50 border-amber-200" },
  { value: "facility", label: "Facility", desc: "AC, water, cleaning, repairs", icon: Wrench, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { value: "hr_request", label: "HR Request", desc: "Documents, letters, queries", icon: FileText, color: "text-red-700 bg-red-50 border-red-200" },
];

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "text-red-700 border-red-200 bg-red-50" },
  { value: "high",   label: "High",   color: "text-orange-700 border-orange-200 bg-orange-50" },
  { value: "medium", label: "Medium", color: "text-amber-700 border-amber-200 bg-amber-50" },
  { value: "low",    label: "Low",    color: "text-emerald-700 border-emerald-200 bg-emerald-50" },
];

export default function RequestNew() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=type, 2=form
  const [type, setType] = useState("");
  const [categories, setCategories] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    category: "",
    subject: "",
    description: "",
    priority: "medium",
    unit: "",
    specific_location: "",
    estimated_cost: "",
    voice_note_url: "",
    items: [],            // for stationary
    specs: {},            // for IT new hardware
    justification: "",
    urgency_reason: "",
    document_purpose: "",
    submit_to_org: "",
    delivery_method: "",
  });
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    api.get("/requests/thresholds").then(({ data }) => setThresholds(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!type) return;
    api.get(`/requests/categories?request_type=${type}`).then(({ data }) => setCategories(data || [])).catch(() => {});
  }, [type]);

  // Compute approval bucket on the fly
  const costNum = parseFloat(form.estimated_cost);
  const bucket = (() => {
    if (!thresholds || isNaN(costNum) || costNum <= 0) return null;
    if (costNum < thresholds.auto_approval) return { label: "Auto-approved", chain: [], tone: "emerald" };
    if (costNum < thresholds.manager_approval) return { label: "Manager approval", chain: ["Manager"], tone: "blue" };
    if (costNum < thresholds.hod_approval) return { label: "HOD approval", chain: ["HOD"], tone: "amber" };
    if (costNum < thresholds.senior_mgmt_approval) return { label: "HOD + Senior Mgt", chain: ["HOD", "Senior Mgt"], tone: "orange" };
    return { label: "Senior Mgt approval", chain: ["Senior Mgt"], tone: "red" };
  })();

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/requests/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [...prev, data]);
      toast.success("File attached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const submit = async () => {
    if (form.subject.trim().length < 3) return toast.error("Subject is required (min 3 chars)");
    if (form.description.trim().length < 10) return toast.error("Description is required (min 10 chars)");
    setSubmitting(true);
    try {
      const payload = {
        request_type: type,
        category: form.category || null,
        subject: form.subject.trim(),
        description: form.description.trim(),
        priority: form.priority,
        unit: form.unit || null,
        specific_location: form.specific_location || null,
        items: (form.items && form.items.length) ? form.items.map((it) => ({
          name: it.name, quantity: parseInt(it.quantity || 1, 10),
          specifications: it.specifications || null,
          estimated_cost_per_unit: it.cost ? parseFloat(it.cost) : null,
        })) : null,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : null,
        voice_note_url: form.voice_note_url || null,
        attachment_ids: attachments.map((a) => a.id),
        specs: type === "it_support" && Object.keys(form.specs || {}).length ? form.specs : null,
        justification: form.justification || null,
        urgency_reason: form.urgency_reason || null,
        document_purpose: form.document_purpose || null,
        submit_to_org: form.submit_to_org || null,
        delivery_method: form.delivery_method || null,
      };
      const { data } = await api.post("/requests", payload);
      toast.success(`Request ${data.request_number} submitted`);
      navigate(`/my-portal/requests/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // STEP 1 — Type Picker
  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="request-new-step1">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">New Request</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>How can we help you?</h1>
          <p className="text-sm text-slate-500 mt-1">Pick a request type to continue.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TYPE_OPTIONS.map((t) => {
            const Icon = t.icon;
            const selected = type === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                className={`text-left rounded-lg border-2 p-4 transition ${selected ? `${t.color} ring-2 ring-offset-1 ring-slate-400` : "border-slate-200 hover:border-slate-300 bg-white"}`}
                data-testid={`type-${t.value}`}
              >
                <Icon className={`h-5 w-5 mb-2 ${selected ? "" : "text-slate-400"}`} />
                <div className="font-semibold text-sm text-slate-900">{t.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate("/my-portal/requests")}>Cancel</Button>
          <Button
            onClick={() => type && setStep(2)}
            disabled={!type}
            className="brand-bg brand-bg-hover text-white"
            data-testid="continue-btn"
          >
            Continue <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  // STEP 2 — Form
  const showCost = type === "it_support" || type === "stationary" || type === "facility";
  const showItems = type === "stationary";
  const showSpecs = type === "it_support";
  const showLocation = type === "facility";
  const showHrFields = type === "hr_request";
  const showJustification = costNum >= 10000;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="request-new-step2">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">{TYPE_OPTIONS.find(t => t.value === type)?.label}</div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>New Request</h1>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        {/* Category */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="mt-1" data-testid="category-select">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.category_name}>
                  {c.category_name}
                  {c.typical_cost_range && <span className="text-[10px] text-slate-400 ml-2">{c.typical_cost_range}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Subject */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Subject *</Label>
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Brief description"
            maxLength={200}
            className="mt-1"
            data-testid="subject-input"
          />
        </div>

        {/* Description */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Description *</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={5}
            placeholder="Explain in detail — what do you need, why, current situation..."
            className="mt-1"
            data-testid="description-input"
          />
          <div className="text-[10px] text-slate-400 mt-0.5">Min 10 characters</div>
        </div>

        {/* Voice */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Voice Description (optional · max 60s)</Label>
          <div className="mt-1.5">
            <VoiceRecorder
              value={form.voice_note_url || null}
              onChange={(url) => setForm({ ...form, voice_note_url: url || "" })}
              uploadEndpoint="/requests/attachments"
              uploadFields={{}}
              urlFromResponse={(d) => d.file_url}
              testIdPrefix="req-voice"
              compact
            />
          </div>
        </div>

        {/* Priority */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Priority *</Label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {PRIORITY_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setForm({ ...form, priority: p.value })}
                className={`rounded border px-2 py-1.5 text-xs font-medium transition ${form.priority === p.value ? p.color : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                data-testid={`priority-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {showItems && (
          <ItemsEditor items={form.items} onChange={(items) => setForm({ ...form, items })} />
        )}

        {showSpecs && (
          <SpecsEditor specs={form.specs} onChange={(specs) => setForm({ ...form, specs })} />
        )}

        {showLocation && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="mt-1" data-testid="unit-select">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sitapura">Sitapura Unit</SelectItem>
                  <SelectItem value="bassi">Bassi Unit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Specific Location</Label>
              <Input
                value={form.specific_location}
                onChange={(e) => setForm({ ...form, specific_location: e.target.value })}
                placeholder="e.g., 3rd floor, Conference Room"
                className="mt-1"
                data-testid="location-input"
              />
            </div>
          </div>
        )}

        {showHrFields && (
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Purpose of Document</Label>
              <Input
                value={form.document_purpose}
                onChange={(e) => setForm({ ...form, document_purpose: e.target.value })}
                placeholder="e.g., Bank loan application, Visa for travel"
                className="mt-1"
                data-testid="doc-purpose-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Submit To</Label>
                <Input
                  value={form.submit_to_org}
                  onChange={(e) => setForm({ ...form, submit_to_org: e.target.value })}
                  placeholder="e.g., HDFC Bank"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Delivery Method</Label>
                <Select value={form.delivery_method} onValueChange={(v) => setForm({ ...form, delivery_method: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hard_copy">Hard copy (collect)</SelectItem>
                    <SelectItem value="email">Email (PDF)</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {showCost && (
          <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Estimated Cost (₹)</Label>
            <Input
              type="number"
              min="0"
              value={form.estimated_cost}
              onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })}
              placeholder="0 = no cost"
              data-testid="cost-input"
            />
            {bucket && (
              <div className={`flex items-start gap-2 p-2 rounded text-xs border bg-${bucket.tone}-50 border-${bucket.tone}-200`} data-testid="approval-preview">
                <AlertCircle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-${bucket.tone}-700`} />
                <div>
                  <div className={`font-semibold text-${bucket.tone}-700`}>{bucket.label}</div>
                  {bucket.chain.length > 0 ? (
                    <div className="text-slate-600 mt-0.5">Approval chain: {bucket.chain.join(" → ")}</div>
                  ) : (
                    <div className="text-slate-600 mt-0.5">No approval needed — direct to team.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {showJustification && (
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Justification (required for &gt; ₹10,000)</Label>
            <Textarea
              value={form.justification}
              onChange={(e) => setForm({ ...form, justification: e.target.value })}
              rows={3}
              placeholder="Why do you need this? Business case, productivity gains, ROI..."
              className="mt-1"
              data-testid="justification-input"
            />
          </div>
        )}

        {/* Attachments */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Attachments (optional, max 25 MB each)</Label>
          <div className="mt-1.5 flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded cursor-pointer hover:bg-slate-50">
              <Upload className="h-3.5 w-3.5" /> Choose files
              <input
                type="file"
                className="hidden"
                multiple
                onChange={(e) => {
                  Array.from(e.target.files || []).forEach(upload);
                  e.target.value = "";
                }}
                data-testid="attachment-input"
              />
            </label>
            {attachments.length > 0 && (
              <span className="text-xs text-slate-500">{attachments.length} file{attachments.length === 1 ? "" : "s"} attached</span>
            )}
          </div>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 px-2 py-1 bg-slate-50 rounded text-xs">
                  <span className="truncate">{a.file_name}</span>
                  <button
                    onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                    className="text-red-700 hover:underline"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/my-portal/requests")}>Cancel</Button>
        <Button
          onClick={submit}
          disabled={submitting || form.subject.trim().length < 3 || form.description.trim().length < 10}
          className="brand-bg brand-bg-hover text-white"
          data-testid="submit-request-btn"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" /> {submitting ? "Submitting..." : "Submit Request"}
        </Button>
      </div>
    </div>
  );
}

function ItemsEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { name: "", quantity: 1, specifications: "", cost: "" }]);
  const update = (idx, patch) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="border border-slate-200 rounded p-3 space-y-3 bg-slate-50/40">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Items Requested</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="add-item-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Item
        </Button>
      </div>
      {(items || []).length === 0 && (
        <div className="text-xs text-slate-400 italic">No items added yet. Either add items or describe in the request body.</div>
      )}
      {(items || []).map((it, idx) => (
        <div key={idx} className="grid grid-cols-12 gap-2 bg-white rounded border border-slate-200 p-2">
          <Input className="col-span-5" placeholder="Item name (e.g., Blue Pen Reynolds 045)"
                 value={it.name} onChange={(e) => update(idx, { name: e.target.value })} data-testid={`item-${idx}-name`} />
          <Input className="col-span-2" type="number" min="1" placeholder="Qty"
                 value={it.quantity} onChange={(e) => update(idx, { quantity: e.target.value })} data-testid={`item-${idx}-qty`} />
          <Input className="col-span-2" type="number" min="0" placeholder="₹/unit"
                 value={it.cost} onChange={(e) => update(idx, { cost: e.target.value })} />
          <Input className="col-span-2" placeholder="Specs"
                 value={it.specifications} onChange={(e) => update(idx, { specifications: e.target.value })} />
          <button type="button" onClick={() => remove(idx)}
                  className="col-span-1 inline-flex items-center justify-center text-red-700 hover:bg-red-50 rounded">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function SpecsEditor({ specs, onChange }) {
  const set = (k, v) => onChange({ ...(specs || {}), [k]: v });
  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50/40">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Specifications (for new hardware)</Label>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Input placeholder="Item type (e.g., Laptop)" value={specs.item_type || ""} onChange={(e) => set("item_type", e.target.value)} />
        <Input placeholder="Brand preference (e.g., Dell)" value={specs.brand || ""} onChange={(e) => set("brand", e.target.value)} />
        <Input placeholder="RAM (e.g., 16 GB)" value={specs.ram || ""} onChange={(e) => set("ram", e.target.value)} />
        <Input placeholder="Storage (e.g., 512 GB SSD)" value={specs.storage || ""} onChange={(e) => set("storage", e.target.value)} />
        <Input placeholder="Graphics (e.g., Dedicated)" value={specs.gpu || ""} onChange={(e) => set("gpu", e.target.value)} />
        <Input placeholder="Budget range (₹50k - ₹70k)" value={specs.budget_range || ""} onChange={(e) => set("budget_range", e.target.value)} />
        <Input className="col-span-2" placeholder="Usage purpose (e.g., Design work)" value={specs.usage || ""} onChange={(e) => set("usage", e.target.value)} />
      </div>
    </div>
  );
}
